/**
 * The connections section of the Overview — what was the Breakthroughs tab.
 *
 * It lives inside the brief now (`ui/Overview.tsx`), as the live form of the
 * report's own "candidate syntheses" part: the same pairs the export prints, with
 * the presses that make, search, build and dismiss them. What moved with it is
 * the rule of its first screen. Before any scan has read the graph it shows one
 * sentence and one press, "Find connections", priced from the prompt it would
 * send, with the guard that says how many claims a scan needs; the
 * dials — temper, model, scope, the pick of explorations — come after, under
 * "Scan again, differently", which only then is true. The study met all fifteen
 * of them before there was anything to scan again.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, Ref } from 'react'
import { entryFor, missingKeyNeed, resolveFor, useStore } from '../core/store'
import {
  BRIDGE_RUNGS,
  LINK_READING_LABEL,
  linkReadingNote,
  OP_PAST,
  TEMPER_LABEL,
  channelOf,
  type Bridge,
  type BridgeEnd,
  type LinkReading,
  type ReasonNode,
  type Session,
  type StoredLinkage,
  type Temper,
} from '../core/types'
import { PROVIDERS, SELECTABLE_MODELS, findModel, fmtUsd, type Role } from '../core/models'
import {
  budgetForEvidenceProfile,
  evidenceProfileCeiling,
  projectScanPromptUsd,
  scanIsReachable,
  startRun,
  specFromTune,
} from '../engine/run'
import { missingKeyLine, pressName, pressTail } from './pressName'
import { focusCardTitle } from './cardFocus'
import { DEFAULT_EVIDENCE_PROFILE, normalizeEvidenceProfile } from '../core/epistemic'
import { Disclosure, Meter } from './components/Controls'
import { Icon } from './components/Icon'
import { Ago } from './components/ago'
import {
  LINK_SCAN_NEED,
  SCAN_EXPLORATIONS,
  SCAN_MIN_CLAIMS,
  SCAN_PER_EXPLORATION,
  SYNTHESIS_NEED,
  realizeBridge,
  runAudit,
  runLinkScan,
  runOp,
  runVerify,
  scanAcrossBlock,
  scanForBridges,
  auditorFor,
} from '../engine/reason'
import {
  BRIDGE_RUNGS_TOTAL,
  RUNG_ACTION,
  RUNG_ACTION_PAID,
  RUNG_LABEL,
  RUNG_RULE,
  STOP_LABEL,
  STOP_RULE,
  bridgeStanding,
  childrenOf,
  sortBridges,
  type BridgeStanding,
} from '../core/rung'
import { listScanCandidates, scanPool, scanPoolKey } from '../core/persist'
import { STATUS_WORD, pressEstimate } from '../core/vocabulary'
import { calendarDay } from '../core/dates'
import { isSameReading, scanOpts, tickedCount, type ScanPick } from './scanPress'
import { openSession } from '../core/autosave'
import { hostOf } from '../core/url'
import { DISPUTED_RULE, restsOnDisputed } from '../core/recordDigest'
import { AskThread } from './AskThread'
import { measuredNoveltyOf, priorArtOf, priorArtText } from '../core/priorWork'
import { fmtRating, selfRatingsOf } from '../core/rewrite'
import { refutedUnder } from '../core/taint'
import { standsOnWord } from '../core/statusWords'

/**
 * What an end can be shown as, and whether it can still be reached.
 *
 * Three cases, and the third is the one the old code got wrong: a local end whose
 * node has been pruned used to make the whole card render `null`, so a candidate
 * the user paid for silently disappeared from a list that still counted it.
 */
type Shown =
  | { at: 'here'; label: string; nodeId: string }
  | { at: 'elsewhere'; label: string; seed: string; sessionId: string; nodeId: string }
  | { at: 'gone'; label: string }

function shownEnd(e: BridgeEnd, nodes: Record<string, ReasonNode>): Shown {
  if (e.where === 'foreign') {
    return {
      at: 'elsewhere',
      label: e.ref.label,
      seed: e.ref.seed,
      sessionId: e.ref.sessionId,
      nodeId: e.ref.nodeId,
    }
  }
  const n = nodes[e.nodeId]
  return n
    ? { at: 'here', label: n.label, nodeId: n.id }
    : { at: 'gone', label: STATUS_WORD.pruned.toLowerCase() }
}

/**
 * The synthesis surface — a join finder, not a leaderboard.
 *
 * Everything else in the tool moves *outward* from the seed. This is the one
 * place that moves inward: it proposes PAIRS of branches that grew far apart and
 * asks whether they are secretly the same problem. Per pair, a paid search asks
 * the literature whether anyone has already joined them; per graph, a re-scan is
 * offered only when it could return something new — same graph, same reading,
 * same pairs, and charging for that would be charging for a re-read. The nodes
 * that cleared on their own survive below, as the smaller list.
 */
/** The reader's calendar day — see `core/dates.ts`. */
const fmtDay = calendarDay

/**
 * The chip tint per reading.
 *
 * `already-made` is the loud one, and it is the ONLY loud one. It is the single
 * reading here built on something a search returned, and the thing it reports is
 * that the pair is spoken for. `unjoined` deliberately gets the quiet signal hue
 * rather than the insight green every other synthesis surface uses: an empty
 * search is not a breakthrough, and colouring it like one would have the palette
 * making a claim the prose spends two sentences refusing to make.
 */
const LINK_TINT: Record<LinkReading, string> = {
  'already-made': 'var(--sem-refuted)',
  unsound: 'var(--sem-weak)',
  thin: 'var(--sem-weak)',
  'no-intermediate': 'var(--sem-weak)',
  unjoined: 'var(--sem-signal)',
}

/**
 * What a link scan found, on the card.
 *
 * The counts are shown next to every term because they are the whole substance
 * of the reading — "appears in 3 of 4 documents on one side and 2 of 5 on the
 * other" is a fact anybody can re-derive from the same results, where "strongly
 * connected" would be this tool grading its own arithmetic.
 */
function Linkage({ l }: { l: StoredLinkage }) {
  return (
    <div className="group" style={{ gap: 'var(--s2)' }}>
      <div className="grow-chip" style={{ '--c-tint': LINK_TINT[l.reading] } as CSSProperties}>
        <i />
        <b>{LINK_READING_LABEL[l.reading]}</b>
        <span>{fmtDay(l.scannedAt)}</span>
      </div>

      {/* One note: on an unsound reading it names the search that stopped and
          what stopped it (`linkReadingNote`), where it used to blame "a search"
          and then list every failure again beneath. */}
      <p className="gnote">{linkReadingNote(l)}</p>

      {l.depth === 'titles' && l.reading !== 'unsound' && (
        <p className="gnote gnote--warn">
          {l.model.name} does not return the text of a page it opens, so every term below was read
          off a title. A shared term this missed is a term nobody counted.
        </p>
      )}

      {l.joinedBy.length > 0 && (
        <ul className="srcs">
          {l.joinedBy.map((s, i) => (
            <li key={i} data-stance="disputes">
              <a href={s.url} target="_blank" rel="noreferrer noopener">
                {/* A title this provider never supplied stands in as the URL, and
                    showing the host twice is noise. Same rule as `SourceList`. */}
                {s.title === s.url ? hostOf(s.url) : s.title}
              </a>
              <span className="srcs-meta">
                {hostOf(s.url)} · {s.via === 'fetch' ? 'opened' : 'from the results'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {l.intermediates.length > 0 && (
        <>
          <span className="eyebrow">In both literatures</span>
          <ul className="lnk-terms">
            {l.intermediates.map((t) => (
              <li key={t.term} data-via={t.term === l.via || undefined}>
                <b>{t.term}</b>
                <span>
                  {t.aDocs} of {l.aDocs} · {t.cDocs} of {l.cDocs}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {l.mechanism && <p className="insp-reasoning">{l.mechanism}</p>}

      {l.queries.length > 0 && (
        <Disclosure
          label="What was searched"
          meta={`${l.queries.length} quer${l.queries.length === 1 ? 'y' : 'ies'}`}
          hint="Queries the model wrote itself, from the two claims and their parents. An absence only means something next to what was looked for."
        >
          <p data-lead>It searched for: {l.queries.map((q) => `“${q}”`).join(', ')}.</p>
          <p>
            Run by {l.model.name} on {fmtDay(l.scannedAt)}. The reading above is a count over what
            those searches returned on that day, not a statement about the literature.
          </p>
        </Disclosure>
      )}
    </div>
  )
}

/**
 * The line on a candidate that rests on a claim a check disputed, or nothing.
 *
 * A scan built a candidate on a claim the person's own check had already read
 * against, and the card said nothing: a check informed only its own subtree.
 * The rule is `DISPUTED_RULE`, mechanical and read off the stored readings, and
 * the hover prints it — a mark that cannot say why it is there is a verdict. It
 * names the nearest such claim, the end itself before anything beneath it. It
 * marks and does not rule: a pair can fuse through a claim a check disputed, and
 * whether it should is the person's call. Foreign ends are not walked — the
 * stored digest of another exploration keeps no reading — and the seed is not
 * either (`restsOnDisputed` says why).
 */
export function DisputedBeneath({
  bridge,
  nodes,
}: {
  bridge: Bridge
  nodes: Record<string, ReasonNode>
}) {
  const beneath = restsOnDisputed(nodes, bridge)
  if (!beneath.length) return null
  const more = beneath.length - 1
  return (
    <div className="gnote gnote--warn" title={`Marked because ${DISPUTED_RULE}.`}>
      Rests on a claim a check disputed: “{beneath[0]!.label}”
      {more ? `, and ${more} more beneath it` : ''}.
    </div>
  )
}

/** The three tempers as chips, writing the same workspace temper the composer's
 *  chip writes — one dial, two handles, never a scan-private shadow copy. */
const TEMPERS: readonly Temper[] = ['grounded', 'balanced', 'speculative']

/**
 * The re-scan control, honest about when a second pass could return anything.
 *
 * Four states: something changed (offer the scan and say what changed), only new
 * nodes (offer the scan and count them), nothing changed (offer nothing — a
 * click toasts that nothing was charged), and running. An across scope with
 * every exploration unticked is the one added state: it would read nothing, so
 * the button says that and spends nothing. What the last scan read comes from
 * `lastScan`, noted in memory when a scan lands; after a reload the note is
 * gone and the control errs toward offering the scan, because "nothing would
 * change" is a claim it can no longer back.
 *
 * Shown only once a scan has read this graph — its eyebrow says "again" — or
 * when the palette's across entry asked for the pick before any scan had, which
 * is the one door that comes here FOR the dials. That door gets the same panel
 * under a heading that does not claim a scan came before.
 */
function ScanPanel({
  session,
  picks,
  setPicks,
  bodyRef,
  first = false,
}: {
  session: Session
  picks: ScanPick
  setPicks: (p: ScanPick) => void
  /** The palette's poke scrolls the panel into view through this. */
  bodyRef?: Ref<HTMLDivElement>
  /** No scan has read this graph yet: the panel is here because somebody asked
   *  for the pick, and "again" would be the one false word on it. */
  first?: boolean
}) {
  const busy = useStore((s) => s.busy)
  const activeOp = useStore((s) => s.activeOp)
  const temper = useStore((s) => s.temper)
  const setTemper = useStore((s) => s.setTemper)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const setRoleModel = useStore((s) => s.setRoleModel)
  const note = useStore((s) => s.lastScan[session.id])
  const toast = useStore((s) => s.toast)

  const lens = useStore((s) => s.lens)
  const scanEntry = entryFor({ modelId, roleModels }, 'scan')

  const total = session.order.length
  // Display only. `total` is what the scan actually reads, and what `ScanNote`
  // was written from, so the comparisons below must keep it — but everywhere
  // else in this app "nodes" means the ones derived from the seed, and a pill
  // reading 21 NODES one panel away from "across 22 nodes" reads as a bug.
  const shown = Math.max(0, total - 1)
  const scanning = busy && activeOp === 'bridge'
  // Read fresh on every render, the sidebar's own precedent: the index is slim
  // rows, and a memo would need an invalidation signal that saves of OTHER
  // explorations never send this panel.
  const others = listScanCandidates(session.id, Number.POSITIVE_INFINITY)
  // What an across press would actually read. Keyed here from index rows where
  // the engine keys from the digests it printed — a row that fails to read back
  // makes the two keys differ, and differing errs toward offering the scan,
  // which is the safe way to be wrong about a re-read.
  const wouldRead =
    picks === null ? [] : scanPool(others, session.id, SCAN_EXPLORATIONS, [...picks])
  const poolKey = scanPoolKey(wouldRead)
  // Priced from the prompt this press would send (`projectScanPromptUsd`). The across
  // half is what the ticked explorations' digests print, so it is read again when
  // the pick, or a save of something picked, changes — not on every render.
  const acrossBlock = useMemo(
    () => (picks === null ? undefined : scanAcrossBlock(session.id, [...picks])),
    // `poolKey` carries each picked exploration's save; `others.length` the
    // library's size, which the corpus's "not read" line counts.
    [session.id, picks, poolKey, others.length],
  )
  const est = useMemo(
    () => projectScanPromptUsd({ modelId, roleModels, lens, temper, session }, acrossBlock),
    [modelId, roleModels, lens, temper, session, acrossBlock],
  )
  const estNote = est !== null ? ` · ≈ ${fmtUsd(est)}` : ''
  // A picked id whose exploration another tab has since forgotten renders no
  // row, so it holds no seat: `ticked` is what the cap
  // and the empty-pick state count, or a phantom would hold the cap closed
  // behind a tooltip pointing at a row that is not there.
  const liveIds = new Set(others.map((e) => e.id))
  const ticked = picks === null ? 0 : tickedCount(picks, liveIds)
  const nothingTicked = picks !== null && ticked === 0
  const sameReading =
    note !== undefined && isSameReading(note, { temper, modelId: scanEntry.id, pool: poolKey })
  // Gated off the empty pick, which keys to the same '' pool a within note
  // holds: "nothing would change" over a pick that reads nothing would be the
  // wrong truth to tell first.
  const dead = !nothingTicked && sameReading && note.nodes === total
  const newOnly = !nothingTicked && sameReading && total > note.nodes
  // One fragment for every sentence that describes a read, so the label shown
  // while money is being spent cannot drop the across half the idle button
  // promised. Every across press rides the panel's own picks — the palette
  // pokes this panel open instead of launching a scan — so this fragment
  // always sees the pool it describes.
  const poolSuffix = wouldRead.length
    ? ` + ${wouldRead.length} exploration${wouldRead.length === 1 ? '' : 's'}`
    : ''

  let label: string
  let meta: string
  if (scanning) {
    label = 'Scanning the graph…'
    meta = `across ${shown} nodes${poolSuffix}`
  } else if (nothingTicked) {
    label = 'Nothing ticked to read'
    meta = 'tick at least one exploration below'
  } else if (dead) {
    label = 'Nothing would change'
    meta = 'same graph, same reading · it would return these pairs'
  } else if (newOnly) {
    label = 'Scan again'
    meta = `${total - note.nodes} new node${total - note.nodes === 1 ? '' : 's'} since the last scan${estNote}`
  } else if (note) {
    const was = findModel(note.modelId)?.name ?? note.modelId
    const parts = [
      ...(note.temper !== temper ? [`${TEMPER_LABEL[note.temper]} → ${TEMPER_LABEL[temper]}`] : []),
      ...(note.modelId !== scanEntry.id ? [`${was} → ${scanEntry.name}`] : []),
      // The pool line states the NEW reading rather than diffing two key
      // strings nobody can parse back into explorations.
      ...(note.pool !== poolKey
        ? [
            wouldRead.length
              ? `reading ${wouldRead.length} other exploration${wouldRead.length === 1 ? '' : 's'}`
              : 'this graph alone',
          ]
        : []),
    ]
    label = `Scan with ${scanEntry.name} at ${TEMPER_LABEL[temper]}`
    meta = `${parts.join(' · ')}${estNote}`
  } else {
    label = `Scan with ${scanEntry.name} at ${TEMPER_LABEL[temper]}`
    meta = `across ${shown} nodes${poolSuffix}${estNote}`
  }

  return (
    <div className="btscan" ref={bodyRef}>
      <div className="tierline">
        <span className="eyebrow">
          {first ? 'Choose what the scan reads' : 'Scan again, differently'}
        </span>
        <span
          className="infobtn"
          title="A second pass over the same graph under the same reading returns the same pairs. Change the reading and it will not."
        >
          <Icon name="info" size={13} />
        </span>
      </div>
      <p className="gnote gnote--quiet">
        {first
          ? 'Which explorations it may read, and the temper and model it reads them under.'
          : 'Same graph, different reading. What you change decides what it finds.'}
      </p>

      <div className="btscan-row">
        <span className="btscan-key">Temper</span>
        <div className="btscan-chips">
          {TEMPERS.map((t) => (
            <button
              key={t}
              className="btchip"
              data-on={t === temper || undefined}
              onClick={() => setTemper(t)}
            >
              {TEMPER_LABEL[t]}
            </button>
          ))}
        </div>
      </div>
      <div className="btscan-row">
        <span className="btscan-key">Model</span>
        <div className="btscan-chips">
          {SELECTABLE_MODELS.map((m) => (
            <button
              key={m.id}
              className="btchip"
              data-on={m.id === scanEntry.id || undefined}
              title={`Route the breakthrough scan to ${m.name}. Routing never falls back — with no ${m.provider} key it refuses and says so.`}
              onClick={() => setRoleModel('scan', m.id === modelId ? null : m.id)}
            >
              {m.name}
            </button>
          ))}
        </div>
      </div>

      <div className="btscan-row">
        <span className="btscan-key">Scope</span>
        <div className="btscan-chips">
          <button
            className="btchip"
            data-on={picks === null || undefined}
            onClick={() => setPicks(null)}
          >
            This exploration
          </button>
          <button
            className="btchip"
            data-on={picks !== null || undefined}
            disabled={!others.length}
            title={
              others.length
                ? 'Read other stored explorations too, and pick which. Every candidate keeps at least one end in this graph.'
                : 'Nothing else in the library holds nodes yet, so there is nothing to read across.'
            }
            onClick={() => {
              // Entering the scope ticks what a bare across scan would read —
              // the newest — so the default press and the picker's default are
              // one fact rather than two. ENTERING only: a re-click of the
              // active chip must not swap a hand pick for the default, which
              // would spend the next press on rows somebody just unticked.
              if (picks !== null) return
              setPicks(new Set(scanPool(others, session.id, SCAN_EXPLORATIONS).map((e) => e.id)))
            }}
          >
            Across explorations
          </button>
        </div>
      </div>
      {picks !== null && (
        <div className="btscan-pool" role="group" aria-label="Explorations this scan may read">
          {others.map((e) => {
            const on = picks.has(e.id)
            const full = !on && ticked >= SCAN_EXPLORATIONS
            return (
              <button
                key={e.id}
                className="btpool-row"
                data-on={on || undefined}
                disabled={full}
                title={
                  full
                    ? `A scan reads at most ${SCAN_EXPLORATIONS} explorations — untick one to make room.`
                    : undefined
                }
                onClick={() => {
                  const next = new Set(picks)
                  if (on) next.delete(e.id)
                  else next.add(e.id)
                  setPicks(next)
                }}
              >
                <i data-on={on || undefined} />
                <span className="btpool-seed">{e.seed}</span>
                <span className="btpool-meta">
                  {e.nodes} node{e.nodes === 1 ? '' : 's'} · <Ago at={e.savedAt} />
                </span>
              </button>
            )
          })}
          <p className="gnote gnote--quiet">
            A scan reads at most {SCAN_EXPLORATIONS} of these — the {SCAN_PER_EXPLORATION} strongest
            nodes of each — and the prompt says how many were left out and why. Every candidate
            keeps at least one end in this graph.
          </p>
        </div>
      )}

      <button
        className="btscan-go"
        data-state={scanning ? 'running' : dead || nothingTicked ? 'dead' : 'live'}
        disabled={busy && !dead}
        onClick={() => {
          if (dead) toast('Same graph, same reading — nothing charged.', 'info')
          // The empty pick goes THROUGH to the engine on purpose: the refusal
          // sentence lives there, once, on the same path "Scan them" lands on.
          else void scanForBridges(scanOpts(picks))
        }}
      >
        <b>
          <Icon name="bridge" size={13} />
          {label}
        </b>
        <span>{meta}</span>
      </button>
    </div>
  )
}

/** A candidate's press under a pass in flight, in the engine's own words for it
 *  (`runLinkScan`, `realizeBridge`): neither waits on the Up next line. */
const LOCK_LINE = 'One thought at a time — let the current pass finish.'

/** What a scan does, in the one sentence the section offers before any has run,
 *  and the preview its fold shows while shut. */
export const CONNECTIONS_LEDE =
  'A scan reads the whole graph and proposes pairs of distant branches that may fuse. It adds no nodes, and whether a pair actually fuses is yours to judge.'

/**
 * Whether a scan has read this graph: a finished, unfailed scan on the ledger —
 * which survives a reload, where the in-memory `lastScan` note does not — or a
 * candidate standing, which only a scan makes. A scan's op is the `bridge` op
 * with no target; realising a pair is a `bridge` op too, aimed at its ends.
 */
export function hasScanned(session: Session): boolean {
  return (
    session.bridges.length > 0 ||
    session.ops.some(
      (o) => o.kind === 'bridge' && o.targetIds.length === 0 && !!o.finishedAt && !o.error,
    )
  )
}

/** Claims a within-graph scan would read, counted as `scanForBridges` counts
 *  them before it refuses: every node on the order but the seed. */
export const scanClaims = (session: Session): number =>
  session.order.filter((id) => session.nodes[id]?.parentId !== null).length

/**
 * The first press: one button, before any scan has read the graph.
 *
 * It is the scan the dials below would configure at their resting values —
 * this graph alone, the workspace temper, the scan's routed model — through the
 * same `scanForBridges` the panel's go button calls, and it stands down under
 * the same lock the panel's does — the Up next line (D162, D167) takes moves,
 * checks and panels, and a scan has never been one of them. The price is the
 * scan's own prompt, priced (`projectScanUsd`) rather than a move's; with no list
 * rate for the routed model the button prints none rather than a wrong one, and
 * with no key for it the button says so and the press leads to the key prompt
 * the engine opens. Below the floor the engine would refuse in a toast; here the
 * button says how many claims it needs, beside a press it does not offer.
 *
 * The line under the label names the model and temper the scan will read
 * under. The empty state once swapped the whole panel for a bare button, and
 * what a scan would cost ON WHICH MODEL then appeared only after one had been
 * paid for; the dials can wait for a second scan, that sentence cannot.
 */
function ScanDoor({ session }: { session: Session }) {
  const busy = useStore((s) => s.busy)
  const scanning = useStore((s) => s.busy && s.activeOp === 'bridge')
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const temper = useStore((s) => s.temper)
  const keyed = useStore((s) => resolveFor(s, 'scan') !== null)
  const lens = useStore((s) => s.lens)
  // The prompt this press would send, priced (`projectScanPromptUsd`): a young graph's
  // scan is a quarter of a move's input, and the press says so.
  const est = useMemo(
    () => projectScanPromptUsd({ modelId, roleModels, lens, temper, session }),
    [modelId, roleModels, lens, temper, session],
  )
  const claims = scanClaims(session)
  const short = claims < SCAN_MIN_CLAIMS

  const price = short
    ? null
    : !keyed
      ? STATUS_WORD.needsKey.toLowerCase()
      : est !== null
        ? pressEstimate(est)
        : null
  const meta = short
    ? `needs ${SCAN_MIN_CLAIMS} claims · this graph has ${claims}`
    : scanning
      ? `reading ${claims} claims`
      : `with ${entryFor({ modelId, roleModels }, 'scan').name} at ${TEMPER_LABEL[temper]}`

  return (
    <button
      className="btscan-go ovw-scan"
      data-state={scanning ? 'running' : short ? 'dead' : 'live'}
      disabled={busy || short}
      title={
        short
          ? `A scan needs ${SCAN_MIN_CLAIMS} claims beyond the question to find distant pairs among. Grow a few more branches first.`
          : busy
            ? 'A scan waits for the pass in flight to land.'
            : undefined
      }
      onClick={() => void scanForBridges()}
    >
      <b>
        <Icon name="bridge" size={13} />
        {scanning ? 'Finding connections…' : 'Find connections'}
        {price && !scanning ? ` · ${price}` : ''}
      </b>
      <span>{meta}</span>
    </button>
  )
}

/**
 * The nodes that cleared on their own — insight, or a self-rated novelty of 0.70
 * and up. The smaller list, below the pairs, because a single high-scoring node
 * is the model admiring one of its own moves and a fused pair is at least a
 * shape the graph did not start with.
 *
 * TWO ORDERS, NEVER ONE. A node a live check re-rated against prior work it could
 * name sorts after every node nothing has re-rated, and among its own kind by the
 * re-rating; the rest sort by the self-rating as before. "Raise oxidation" led this
 * list at 0.75 after its own check had re-rated it 0.30 against a paper that had
 * already done it. Sorting everything by the re-rating where there is one would
 * put two different judgements on one axis — a number that had prior work to look
 * at against one that never looked, often from another model — so the groups are
 * ordered apart, and every row prints its own self-rating with the prior-art line
 * beside it rather than a blend of the two — on one scale, "0.75" (`fmtRating`),
 * where the row had said "novelty 75" over "re-rated 0.75 → 0.30" and "66%
 * confidence" under both.
 *
 * A claim the reader rewrote has no self-rating: the model rated the wording that
 * was replaced (`selfRatingsOf`, which the card and the export read too). So its
 * old novelty neither clears it nor sorts it — only its kind can bring it here —
 * and its row says why it carries no number.
 */
export function clearedGroups(session: Session): {
  unmeasured: ReasonNode[]
  rerated: ReasonNode[]
} {
  const all = session.order
    .map((id) => session.nodes[id])
    .filter((n): n is ReasonNode => !!n && n.parentId !== null && !n.terminal)
    // Conjectures have their own section, ordered by registration (D288). Left
    // here they would be sorted by their self-rated novelty under a heading that
    // says they cleared — the ranking and the verdict that section refuses them.
    .filter((n) => n.kind !== 'conjecture')
    .filter((n) => channelOf(n) === 'insight' || (selfRatingsOf(n)?.novelty ?? 0) >= 0.7)
  const measured = new Map(all.map((n) => [n.id, measuredNoveltyOf(n)]))
  const own = (n: ReasonNode) => selfRatingsOf(n)?.novelty ?? -1
  return {
    unmeasured: all.filter((n) => measured.get(n.id) === undefined).sort((a, b) => own(b) - own(a)),
    rerated: all
      .filter((n) => measured.get(n.id) !== undefined)
      .sort((a, b) => measured.get(b.id)! - measured.get(a.id)!),
  }
}

function ClearedNodes({ session }: { session: Session }) {
  const select = useStore((s) => s.select)
  const revealNode = useStore((s) => s.revealNode)
  const { unmeasured, rerated } = useMemo(() => clearedGroups(session), [session])
  const count = unmeasured.length + rerated.length

  const row = (n: ReasonNode, i: number) => {
    const ch = channelOf(n)
    const art = priorArtOf(n)
    const rated = selfRatingsOf(n)
    // A node that cleared on its own ratings may still stand on a claim since
    // refuted — a synthesis joined from a refuted branch leads this list at full
    // novelty — and the row says so in the card's words rather than let its meter
    // read as a finding that stands.
    const refuted = refutedUnder(session, n.id)
    return (
      <button key={n.id} className="btnode" onClick={() => (revealNode(n.id), select(n.id))}>
        <span className="btnode-ix" style={{ color: `var(--sem-${ch})` }}>
          {String(i + 1).padStart(2, '0')}
        </span>
        <span className="btnode-body">
          <b>{n.label}</b>
          <span className="btnode-lead">{n.claim}</span>
          {rated && (
            <span className="btnode-meterrow">
              <Meter value={rated.novelty} tint="var(--sem-insight)" />
              <span className="btnode-nov">novelty {fmtRating(rated.novelty)}</span>
            </span>
          )}
          {/* Text, not a link: the row is a button, and the card it opens links
              the source. */}
          {art && <span className="btnode-meta">{priorArtText(art)}</span>}
          {refuted && (
            <span className="btnode-meta btnode-meta--warn">{standsOnWord(refuted)}</span>
          )}
          <span className="btnode-meta">
            {rated
              ? `depth ${n.depth} · confidence ${fmtRating(rated.confidence)} — its own ratings`
              : `depth ${n.depth} · no ratings: the model rated the wording you replaced`}
          </span>
        </span>
      </button>
    )
  }

  return (
    <div className="group" style={{ gap: 'var(--s2)' }}>
      <span className="eyebrow">
        {count
          ? `${count} node${count === 1 ? '' : 's'} that cleared on ${
              count === 1 ? 'its' : 'their'
            } own`
          : 'No single node has cleared yet'}
      </span>
      {unmeasured.map((n, i) => row(n, i))}
      {rerated.length > 0 && (
        <span className="eyebrow">
          {`${rerated.length} re-rated against prior work a check found`}
        </span>
      )}
      {rerated.map((n, i) => row(n, unmeasured.length + i))}
    </div>
  )
}

/**
 * The ladder, in the card's head where the score used to be.
 *
 * Seven marks, one per rung, filled where earned — gaps included, so a
 * candidate that skipped a rung shows the hole rather than a tidy bar — and
 * the rung it stands on in a word. The title carries the predicate that earned
 * it, because a mark with no rule behind it is a verdict.
 */
function Ladder({ st }: { st: BridgeStanding }) {
  const word = st.stop ? STOP_LABEL[st.stop] : RUNG_LABEL[st.rung]
  const rule = st.stop ? STOP_RULE[st.stop] : RUNG_RULE[st.rung]
  return (
    <span
      className="bt-ladder"
      title={`${st.earned.length} of ${BRIDGE_RUNGS_TOTAL} rungs earned. ${word}: ${rule}`}
    >
      <span className="bt-rungs" aria-hidden>
        {BRIDGE_RUNGS.map((r) => (
          <i
            key={r}
            data-earned={st.earned.includes(r) || undefined}
            data-current={r === st.rung || undefined}
          />
        ))}
      </span>
      <b className="bt-rung" data-stop={st.stop ?? undefined}>
        {word}
      </b>
    </span>
  )
}

/**
 * The standing, spelled out on the open card: the rule that earned the rung,
 * the hold if an instrument ran and did not say yes, the model's own hint
 * labelled as such — and the one press that climbs, where the card does not
 * already carry it. The link scan and the realisation have their own buttons
 * below, so those two rungs offer nothing here rather than a duplicate.
 */
function Standing({
  st,
  b,
  busy,
  onClimb,
}: {
  st: BridgeStanding
  b: Bridge
  busy: boolean
  onClimb: () => void
}) {
  const action = st.stop ? null : RUNG_ACTION[st.rung]
  const own = action && action.kind !== 'linkScan' && action.kind !== 'realise' ? action : null
  const paid = own ? RUNG_ACTION_PAID[own.kind] : false
  // The role this press would actually go to. Readiness cannot come from the
  // SELECTION: a check is routed to the `check` role and a cross-examination to
  // another house entirely, and either can be callable when the selected model
  // is not, or the reverse. Asking `useCanCall` enabled a button that then
  // refused, and disabled one whose own route was ready.
  const role: Role | null =
    own?.kind === 'verify' ? 'check' : own?.kind === 'challenge' ? 'reason' : null
  // Whether it resolves, never the resolution: both build a new object per call
  // once a key is set, and a selector whose answer never matches its last one
  // re-renders until React gives up (D328).
  const route = useStore((s) =>
    own?.kind === 'audit'
      ? !!st.nodeId && auditorFor(s, st.nodeId) !== null
      : !!role && resolveFor(s, role) !== null,
  )
  const routeName = useStore((s) => (role ? entryFor(s, role).name : null))
  const ready = !paid || route
  return (
    <div className="bt-standing">
      <p className="gnote">
        <b>{st.stop ? STOP_LABEL[st.stop] : RUNG_LABEL[st.rung]}.</b>{' '}
        {st.stop ? STOP_RULE[st.stop] : RUNG_RULE[st.rung]} Scan hint {b.score.toFixed(2)} — the
        proposing model’s own rating, which orders nothing.
      </p>
      {st.held && st.next && (
        <p className="gnote gnote--warn">
          Held below {RUNG_LABEL[st.next].toLowerCase()}: {st.held}.
        </p>
      )}
      {own && (
        <button
          className="btn btn--block"
          disabled={busy || (paid && !ready)}
          onClick={onClimb}
          title={
            paid
              ? ready
                ? `${own.label} — a paid pass on the synthesis, at the rates the node card quotes for it.`
                : own.kind === 'audit'
                  ? 'A cross-examination needs a key for a second provider — the point is a model from another company.'
                  : `This pass is routed to ${routeName ?? 'your selected model'}, which needs its key.`
              : 'Opens the outcome form on the synthesis. Nothing runs.'
          }
        >
          {own.label}
          {st.next ? ` · toward ${RUNG_LABEL[st.next].toLowerCase()}` : ''}
        </button>
      )}
    </div>
  )
}

export function Breakthroughs({
  onReveal,
}: {
  /** Opens the section this renders in and brings it on screen. The palette's
   *  across entry is a door INTO the pick, and a pick inside a shut fold would
   *  be a door onto a wall. */
  onReveal?: () => void
}) {
  const session = useStore((s) => s.session)
  const bridges = useStore((s) => s.session.bridges)
  const nodes = useStore((s) => s.session.nodes)
  const busy = useStore((s) => s.busy)
  // Each press is ready by the route its pass resolves (`resolveFor`, as
  // `routeReady` reads the card's): a link scan searches on the check route,
  // building one reasons on the reasoning route. The search read the selection's
  // key once, so it greyed out over a keyless selection whose check route could
  // run it, and stood ready over a routed check that would refuse (invariant 4).
  const checkReady = useStore((s) => resolveFor(s, 'check') !== null)
  const reasonReady = useStore((s) => resolveFor(s, 'reason') !== null)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const linkRoute = useStore((s) => resolveFor(s, 'check') !== null)
  const linkRouteName = useStore((s) => entryFor(s, 'check').name)
  /**
   * And the PURSUIT's own routes, which are not the search's.
   *
   * The two presses sat behind one `ready` flag, so correcting it for the search
   * moved the same error onto the pursuit rather than removing it: a keyed
   * checker with an unkeyed reasoning selection enabled a run `startRun` refuses
   * on its first guard. One flag for two different questions was the bug both
   * times; there are two now.
   *
   * A pursue run always reasons and always grounds the synthesis with a source
   * check before anything under it, so those two are unconditional. The panel
   * and the cross-examination are the composer's dials, asked about only when
   * the dial is above zero — the same rule `rolesInPlay` applies in `startRun`,
   * and the reason a run that will make no panel is not refused for a panel key.
   *
   * Each is whether the route resolves, never the resolution: `resolveFor`
   * builds a new object per call once a key is set, and a selector whose answer
   * never matches its last one re-renders until React gives up (D328).
   */
  const reasonRoute = useStore((s) => resolveFor(s, 'reason') !== null)
  const panelRoute = useStore((s) => resolveFor(s, 'panel') !== null)
  // The CLOSING SCAN's role, which is a different one from the check the
  // synthesis is grounded with — they were the same variable once, under a name
  // that said "scan" and held the check, so the pursuit asked about the check
  // twice and never about the scan `startRun` refuses without.
  const scanRoute = useStore((s) => resolveFor(s, 'scan') !== null)
  const reasonRouteName = useStore((s) => entryFor(s, 'reason').name)
  const scanRouteName = useStore((s) => entryFor(s, 'scan').name)
  // The auditor depends on the TARGET's branch, so it is asked per candidate,
  // from the fields it reads. A selector returning this function was a new
  // answer on every call, and looped the same way.
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const auditorFrom = (id: string) =>
    auditorFor({ modelId, keys, customBase, customModel, session }, id)
  const evidenceProfile = useStore((s) => s.session.research?.evidenceProfile)
  const total = useStore((s) => s.session.order.length)
  const select = useStore((s) => s.select)
  const dropBridge = useStore((s) => s.dropBridge)
  const askAbout = useStore((s) => s.askAbout)
  const chat = useStore((s) => s.chat)
  const revealNode = useStore((s) => s.revealNode)
  const ops = useStore((s) => s.session.ops)
  const note = useStore((s) => s.lastScan[session.id])
  const pokeRecord = useStore((s) => s.pokeRecord)
  const runTune = useStore((s) => s.runTune)
  const runCeiling = useStore((s) => s.runCeiling)
  const liveRun = useStore((s) => s.run)

  /** Where each candidate stands, and the list in ladder order — `core/rung.ts`.
   *  Recomputed when the graph changes and not only the list, because a record
   *  landing on a synthesis moves its candidate. */
  const standings = useMemo(() => {
    const kids = childrenOf(nodes)
    return new Map(bridges.map((b) => [b.id, bridgeStanding(b, nodes, kids)] as const))
  }, [bridges, nodes])
  const ordered = useMemo(() => sortBridges(bridges, nodes), [bridges, nodes])

  /**
   * The press that climbs to the next rung, dispatched here and nowhere else.
   *
   * The descriptor is data (`RUNG_ACTION`); this switch is the one place it
   * becomes a call, and every arm that spends is a pass the node card already
   * offers under the same verb — so the ladder adds no way to spend, only a
   * way to reach the ways that are already there.
   */
  const climb = (b: Bridge, st: BridgeStanding) => {
    const a = RUNG_ACTION[st.rung]
    if (!a || st.stop) return
    switch (a.kind) {
      case 'linkScan':
        void runLinkScan(b.id)
        return
      case 'realise':
        void realizeBridge(b)
        return
      case 'challenge':
        if (st.nodeId) void runOp('challenge', st.nodeId)
        return
      case 'audit':
        if (st.nodeId) void runAudit(st.nodeId)
        return
      case 'verify':
        if (st.nodeId) void runVerify(st.nodeId)
        return
      case 'record':
        if (st.nodeId) {
          revealNode(st.nodeId)
          select(st.nodeId)
          pokeRecord()
        }
        return
    }
  }

  /** The open pair — the first with evidence, until the user says otherwise. */
  const [open, setOpen] = useState<string | null>(() => bridges.find((b) => b.linkage)?.id ?? null)

  /** The scan's pick — see `ScanPick`. Held here rather than in the panel so
   *  "Scan them" repeats the reading the panel configured. */
  const [picks, setPicks] = useState<ScanPick>(null)
  /** The palette asked for the dials before any scan had read this graph. */
  const [dialsAsked, setDialsAsked] = useState(false)
  // A pick names OTHER explorations as seen from this one, so it does not
  // survive switching graphs: the same ids mean something different from a new
  // vantage, and a stale set would sit ticked over rows nobody chose. The ask
  // for the dials was about that graph too.
  useEffect(() => {
    setPicks(null)
    setDialsAsked(false)
  }, [session.id])

  // The palette's across entry lands here as a one-shot ask, consumed on the
  // way in and cleared — `runFocusId`'s contract, because the poke MOUNTS this
  // surface: a `sigRewrite`-style counter read at mount is a signal the mount
  // has already seen, and the seed would be swallowed exactly when the tab was
  // closed, which is the palette's normal case. It seeds the default pick only
  // when no pick stands: the poke is a door, and a door must not rewrite what
  // somebody already chose behind it.
  const scanAcrossAsk = useStore((s) => s.scanAcrossAsk)
  const clearScanAcross = useStore((s) => s.clearScanAcross)
  const scanRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (scanAcrossAsk === null) return
    clearScanAcross()
    // An ask names the graph it was poked on, and any other graph drops it
    // unconsumed: a flag that outlived its moment must not enter a scope on a
    // session the press never referred to.
    if (scanAcrossAsk !== session.id) return
    const cands = listScanCandidates(session.id, SCAN_EXPLORATIONS)
    // With nothing else in the library to read, the scope stays within — the
    // panel still scrolls into view, where the disabled Across chip says why
    // in its own words. Seeding an empty pick instead would light a chip its
    // own guard disables, over a plea to tick a row that is not there.
    if (cands.length) setPicks((prev) => prev ?? new Set(cands.map((e) => e.id)))
    // The poke asked for the dials, so they show even before a first scan, and
    // the fold they sit in opens.
    setDialsAsked(true)
    onReveal?.()
    // One frame later, so the unfolding pool list has a height to scroll to.
    requestAnimationFrame(() => scanRef.current?.scrollIntoView({ block: 'nearest' }))
  }, [scanAcrossAsk, clearScanAcross, session.id, onReveal])

  /** Nodes landed since the last scan: from the in-memory note when one exists,
   *  else counted against the last scan op's clock — that half survives a
   *  reload because the op does. */
  const fresh = useMemo(() => {
    const baseline =
      note?.nodes ??
      (() => {
        const scan = [...ops].reverse().find((o) => o.kind === 'bridge' && o.finishedAt)
        if (!scan) return null
        const t = scan.finishedAt ?? scan.startedAt
        return session.order.filter((id) => (nodes[id]?.createdAt ?? 0) <= t).length
      })()
    return baseline === null ? 0 : Math.max(0, total - baseline)
  }, [note, ops, session.order, nodes, total])

  // A conversation whose candidate has since been dismissed or realised. It has
  // no card to sit under any more, so `AskThread` shows its own "no longer in
  // the list" note and the Clear that releases the pin — and it has to be
  // reachable from BOTH shapes this surface takes, because dismissing the last
  // candidate is exactly the press that empties the list.
  const orphanChat = chat?.kind === 'bridge' && !bridges.some((b) => b.id === chat.id) ? chat : null

  // Before any scan, one sentence and one press (`ScanDoor`); the dials wait
  // for a scan to have read the graph, when "again, differently" is true. The
  // empty list AFTER a scan keeps them: every pair was built or put away, or
  // none fused, and the next reading is the thing left to choose.
  const scanned = hasScanned(session) || note !== undefined

  if (!bridges.length) {
    return (
      <div className="ovw-conn">
        {orphanChat && <AskThread target={orphanChat} detached />}
        {scanned ? (
          <>
            <p className="gnote">
              No candidate is waiting: the scans so far proposed no pair that fused cleanly, or
              every pair they proposed has been built or put away.
            </p>
            <ScanPanel session={session} picks={picks} setPicks={setPicks} bodyRef={scanRef} />
          </>
        ) : (
          <>
            <p className="gnote">{CONNECTIONS_LEDE}</p>
            {dialsAsked ? (
              <ScanPanel
                session={session}
                picks={picks}
                setPicks={setPicks}
                bodyRef={scanRef}
                first
              />
            ) : (
              <ScanDoor session={session} />
            )}
          </>
        )}
        <ClearedNodes session={session} />
      </div>
    )
  }

  const searched = bridges.filter((b) => b.linkage).length

  return (
    <div className="ovw-conn">
      <p className="gnote">
        Pairs of distant branches that may fuse, listed by what each has earned: a search, a
        synthesis, a challenge, another provider, a source check, an observation. The model’s own
        rating of a pair sits inside its card and orders nothing.
      </p>

      {/* The scan is the one pass that reads the whole graph and produces no
          node, so its reasoning is otherwise unreachable. The meta carries the
          provenance line: proposed from the graph alone, searched only where a
          pair's own paid step has run. */}
      <ScanTrace ops={ops} searched={searched} />

      {fresh > 0 && (
        <div className="btgrow">
          <i />
          <span>
            {fresh} node{fresh === 1 ? ' has' : 's have'} been added since these pairs were made.
          </span>
          <button disabled={busy} onClick={() => void scanForBridges(scanOpts(picks))}>
            Scan them
          </button>
        </div>
      )}

      {ordered.map((b) => {
        const st = standings.get(b.id)!
        const ends = [shownEnd(b.a, nodes), shownEnd(b.b, nodes)]
        // A pruned local end cannot be realised — there is nothing to attach the
        // synthesis to. The card still renders and says so, because a candidate
        // that vanishes without explanation reads as a bug.
        const realisable = !ends.some((e) => e.at === 'gone')
        // The presses' names: the word, then "needs a key" where the route has
        // none (`pressTail`). Neither carries an estimate — nothing prices a
        // link scan or a synthesis on its own — so a keyed one reads its word.
        const buildWord = 'Build this synthesis'
        const buildTail = pressTail({ ready: reasonReady, est: null })
        const searchWord = b.linkage ? 'Search again' : 'Search for bridge'
        const searchTail = pressTail({ ready: checkReady, est: null })
        const buildNeed = missingKeyNeed({ modelId, roleModels }, 'reason', SYNTHESIS_NEED)
        const searchNeed = missingKeyNeed({ modelId, roleModels }, 'check', LINK_SCAN_NEED)
        const isOpen = open === b.id
        const goTo = (e: Shown) => {
          if (e.at === 'here') {
            revealNode(e.nodeId)
            select(e.nodeId)
          } else if (e.at === 'elsewhere') {
            openSession(e.sessionId, e.nodeId)
          }
        }
        return (
          <div key={b.id} className="bt" data-open={isOpen || undefined}>
            <button
              className="bt-main"
              onClick={() => setOpen(isOpen ? null : b.id)}
              aria-expanded={isOpen}
            >
              <div className="bt-head">
                <span className="eyebrow" style={{ color: 'var(--ink-insight)' }}>
                  Synthesis
                </span>
                <Ladder st={st} />
              </div>
              <div className="bt-title">{b.title}</div>
              <div className="bt-why">{b.why}</div>
              <div className="bt-ends">
                <span data-gone={ends[0]!.at === 'gone' || undefined}>{ends[0]!.label}</span>
                <Icon name="bridge" size={13} />
                <span data-gone={ends[1]!.at === 'gone' || undefined}>{ends[1]!.label}</span>
              </div>
              {ends.map((e, i) =>
                e.at === 'elsewhere' ? (
                  <div key={i} className="bt-from">
                    {e.label} comes from your exploration of “{e.seed}”.
                  </div>
                ) : null,
              )}
              <DisputedBeneath bridge={b} nodes={nodes} />
            </button>

            {isOpen && (
              <>
                <div className="bt-controls">
                  {ends.map((e, i) => (
                    <button
                      key={i}
                      className="btn"
                      disabled={e.at === 'gone'}
                      title={
                        e.at === 'elsewhere'
                          ? 'Opens that exploration and selects the node'
                          : undefined
                      }
                      onClick={() => goTo(e)}
                    >
                      {e.at === 'gone'
                        ? i === 0
                          ? 'First is pruned'
                          : 'Second is pruned'
                        : e.at === 'elsewhere'
                          ? i === 0
                            ? 'Open the first'
                            : 'Open the second'
                          : i === 0
                            ? 'Go to first'
                            : 'Go to second'}
                    </button>
                  ))}
                  {/* Before "realise", not after: whether a pair actually fuses is
                      the judgement this tool leaves to a person, and a question is
                      the cheap way to make it. Realising costs a pass and a step of
                      history; asking costs neither. */}
                  <button
                    className="btn"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => askAbout({ kind: 'bridge', id: b.id })}
                    title="A question about this pair, answered from the graph. It realises nothing."
                  >
                    Ask
                  </button>
                  <button
                    className="btn"
                    style={{ color: 'var(--text-3)' }}
                    onClick={() => dropBridge(b.id)}
                    aria-label="Dismiss this candidate"
                    title="Put this candidate away"
                  >
                    <Icon name="close" size={13} />
                  </button>
                </div>

                <Standing st={st} b={b} busy={busy} onClimb={() => climb(b, st)} />

                {st.nodeId ? (
                  <button
                    className="btn btn--outline btn--block"
                    onClick={() => {
                      revealNode(st.nodeId!)
                      select(st.nodeId!)
                    }}
                    title="The synthesis this pair was built into"
                  >
                    <Icon name="bridge" size={13} />
                    Go to the synthesis
                  </button>
                ) : (
                  // Shut by the lock without leaving the tab order (D225): its own
                  // pass takes the lock, and `disabled` under the key that pressed it
                  // dropped focus on the page body. A press under the lock says why
                  // (`realizeBridge`). When the synthesis lands the candidate goes,
                  // and focus goes to the card of what replaced it.
                  <button
                    className="btn btn--outline btn--block"
                    onClick={(e) => {
                      const pressed = e.currentTarget
                      void realizeBridge(b).then(() => {
                        const id = useStore.getState().selectedId
                        if (pressed.isConnected || !id) return
                        const here = document.activeElement
                        if (here && here !== document.body) return
                        focusCardTitle(id)
                      })
                    }}
                    disabled={!realisable}
                    aria-disabled={busy || undefined}
                    aria-label={pressName(buildWord, buildTail)}
                    title={
                      !realisable
                        ? 'One side of this pair has been pruned, so there is nothing to attach it to.'
                        : busy
                          ? LOCK_LINE
                          : reasonReady
                            ? 'Build this synthesis into the graph'
                            : missingKeyLine(PROVIDERS[buildNeed.provider], buildNeed.need)
                    }
                  >
                    <Icon name="bridge" size={13} />
                    {pressName(buildWord, buildTail)}
                  </button>
                )}

                {/* The run that works this candidate and nothing else: the two
                    branches it fused and the synthesis, grounded first — see
                    `RunSpec.pursue`. Offered once realised and only while the
                    candidate is live; the dials come from the composer, the
                    same ones every run reads. */}
                {st.nodeId &&
                  !st.stop &&
                  (() => {
                    // Every route this particular run would take, resolved here
                    // because the auditor depends on the TARGET's branch and so
                    // cannot be a hook at the top of the component.
                    // The budget this run will actually have, not the one the dials ask
                    // for: a durable evidence profile can take panels and
                    // cross-examinations to zero, and requiring a key for a pass
                    // the profile has already ruled out is the same wrong answer
                    // `auditWillRun` was added to stop inside `startRun`.
                    // The WHOLE tune, through the same function the composer
                    // uses. This block held two short copies of it — one for
                    // the budget here and one for the press below — and both
                    // forwarded the four counts and dropped sequential
                    // execution, progressive detail, a custom ladder or depth
                    // and any stop condition the person had typed. Built once,
                    // so the figure this pre-flight checks and the run that
                    // starts cannot describe different runs.
                    const spec = {
                      ...specFromTune(runTune),
                      pursue: b.id,
                      usdMax: runCeiling ? Number(runCeiling) : null,
                    }
                    const profile = normalizeEvidenceProfile(
                      evidenceProfile ?? DEFAULT_EVIDENCE_PROFILE,
                    )
                    const budget = budgetForEvidenceProfile(
                      spec,
                      profile,
                      evidenceProfileCeiling(spec.usdMax, profile),
                    )
                    const missing = !reasonRoute
                      ? reasonRouteName
                      : // A pursue run grounds the synthesis with a source check
                        // before anything under it, so the check route is
                        // certain wherever the budget allows one at all.
                        budget.checks > 0 && !linkRoute
                        ? linkRouteName
                        : scanIsReachable(budget) && !scanRoute
                          ? scanRouteName
                          : (budget.panels ?? 0) > 0 && !panelRoute
                            ? 'panel'
                            : (budget.audits ?? 0) > 0 &&
                                st.nodeId &&
                                auditorFrom(st.nodeId) === null
                              ? 'second-provider'
                              : null
                    return (
                      <button
                        className="btn btn--block"
                        disabled={busy || missing !== null || !!liveRun}
                        onClick={() => void startRun(spec)}
                        title={
                          liveRun
                            ? 'A run is already going.'
                            : missing === null
                              ? // The last clause is conditional because the stop is:
                                // `startRun` derives the next-rung condition only when
                                // nobody named one, and a condition the person typed is
                                // theirs. Now that one can actually reach this run, the
                                // sentence has to follow it.
                                `A run confined to the two branches this pair fused and to the synthesis itself. It checks the synthesis against sources first, then works the branches, and stops ${runTune.until ? 'on the condition you set for the next run' : 'when the candidate earns its next rung'}.`
                              : missing === 'panel'
                                ? 'This run is set to hold panels, and the panel role has no key. Turn panels off in the composer, or add a key.'
                                : missing === 'second-provider'
                                  ? 'This run is set to cross-examine, which needs a key for a model from another company. Turn cross-examination off in the composer, or add a key.'
                                  : `A run makes a pass for every move and grounds the synthesis first, so it needs your ${missing} key.`
                        }
                      >
                        <Icon name="play" size={13} />
                        Pursue this candidate
                      </button>
                    )
                  })()}

                {b.linkage && <Linkage l={b.linkage} />}

                {/* Below the card body rather than beside "build", because it
                    answers a different question. Building asks whether the pair is
                    worth committing to; this asks whether anybody has built it
                    already, and the second is cheap to run before the first. */}
                {/* Keyless on its route it still presses, and the press opens
                    that route's key field (`runLinkScan`, D202) — the sentence
                    its title says first. Under its own lock, as Build above. */}
                <button
                  className="btn btn--block"
                  disabled={!realisable}
                  aria-disabled={busy || undefined}
                  aria-label={pressName(searchWord, searchTail)}
                  title={
                    !realisable
                      ? 'One side of this pair has been pruned, so there is nothing to search for.'
                      : busy
                        ? LOCK_LINE
                        : checkReady
                          ? 'Look for published work that treats both claims together.'
                          : missingKeyLine(PROVIDERS[searchNeed.provider], searchNeed.need)
                  }
                  onClick={() => void runLinkScan(b.id)}
                >
                  <Icon name="search" size={13} />
                  {pressName(searchWord, searchTail)}
                </button>
              </>
            )}
            {/* The conversation about THIS pair, under the card it is about —
                the same rule the thread follows for a node. A candidate is not
                on any path, so this is the only place it could honestly sit;
                `Thread` resolves an open bridge conversation to this surface
                for exactly that reason. */}
            {chat?.kind === 'bridge' && chat.id === b.id && <AskThread target={chat} />}
          </div>
        )
      })}

      {/* Outside the list, because the block above lives inside it and cannot
          render once the pair is gone. See `orphanChat` — the empty-list shape
          above carries the same mount, since dismissing the last candidate is
          the very press that takes this surface there. */}
      {orphanChat && <AskThread target={orphanChat} detached />}

      <ScanPanel session={session} picks={picks} setPicks={setPicks} bodyRef={scanRef} />

      <ClearedNodes session={session} />
    </div>
  )
}

function ScanTrace({ ops, searched }: { ops: Session['ops']; searched: number }) {
  const scan = [...ops]
    .reverse()
    .find((o) => o.kind === 'bridge' && o.produced.length === 0 && o.thinking)
  return (
    <>
      {/* The sheet's provenance row: how the pairs came to exist, and the one
          sentence that keeps the list honest — proposed from the graph alone,
          searched only where a pair's own paid step has run. */}
      <div className="tierline">
        <span className="eyebrow">How these were found</span>
        <span
          className="infobtn"
          title="Every pair here was proposed from the graph alone. No search was run to find them — that is a separate, paid step per pair."
        >
          <Icon name="info" size={13} />
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            fontWeight: 300,
            color: 'var(--text-3)',
            whiteSpace: 'nowrap',
          }}
        >
          {searched ? `${OP_PAST.bridge} · ${searched} searched` : OP_PAST.bridge}
        </span>
      </div>
      {scan?.thinking && (
        <Disclosure
          label="The scan’s reasoning"
          hint="The model's deliberation on the pass that proposed these pairs"
        >
          {scan.thinking.split('\n\n').map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </Disclosure>
      )}
    </>
  )
}
